import React, { Suspense, useCallback, useEffect, useRef } from 'react';

// eslint-disable-next-line
import "swiper/css/bundle";
import './Swiper.css';

import { BrowserRouter, Routes, Route } from 'react-router-dom';
import './App.css';
import './App.characterResolution.css'
import './App.form.css';
import './index.css';

import {APIProvider} from '@vis.gl/react-google-maps';
import {APP_TYPES, googleMapsApiKey} from './utils/constants';
import { LoadScript } from '@react-google-maps/api';
import {QueryClientProvider} from "@tanstack/react-query";
import {clearQueryClientCache, queryClient} from "./utils/query-client";
import {AppType, AppTypeEnum} from "./utils/types";
import {CattownUser} from "./utils/user";
import Loading from "./components/Loading";

export const routes = { //Will be use for auth
  index: '/',
  sponsors: '/sponsors',
  language: '/language',
  province: '/province',
  instructions: '/instructions',
  city: '/city',
  character: '/character',
  character_custom: '/character/custom',
  station: {
    station1A: '/s/station1/A',
    station1B: '/s/station1/B',
    communicationA: '/s/communication/A',
    communicationB: '/s/communication/B',
    relationshipA: '/s/relationship/A',
    relationshipB: '/s/relationship/B',
    musicA: '/s/4music/A',
    musicB: '/s/4music/B',
    fashionA: '/s/4fashion/A',
    fashionB: '/s/4fashion/B',
    sportA: '/s/4sport/A',
    sportB: '/s/4sport/B',
    geekA: '/s/4geek/A',
    geekB: '/s/4geek/B',
    endA: '/s/end/A',
    endB: '/s/end/B',
    endC: '/s/end/C',
    endD: '/s/end/D',
  }
}

// ✅ helper for lazy loading with optional preload
function lazyWithPreload<T extends React.ComponentType<any>>(
    factory: () => Promise<{ default: T }>
) {
  const Component = React.lazy(factory);
  (Component as any).preload = factory; // attach preload method
  return Component as React.LazyExoticComponent<T> & { preload: () => Promise<{ default: T }> };
}

// ✅ define all screens
export const SCREENS = {
  Welcome:                lazyWithPreload(() => import(/* webpackChunkName: "welcome" */ "./pages/Welcome")),
  Sponsors:               lazyWithPreload(() => import(/* webpackChunkName: "sponsors" */ "./pages/CattownSponsors")),
  SelectLanguage:         lazyWithPreload(() => import(/* webpackChunkName: "language" */ "./pages/SelectLanguage")),
  ProvinceMessage:        lazyWithPreload(() => import(/* webpackChunkName: "province" */ "./pages/ProvinceMessage")),
  Instructions:           lazyWithPreload(() => import(/* webpackChunkName: "instructions" */ "./pages/Instrusctions")),
  SelectCity:             lazyWithPreload(() => import(/* webpackChunkName: "select-city" */ "./pages/SelectCity")),
  SelectCharacter:        lazyWithPreload(() => import(/* webpackChunkName: "select-character" */ "./pages/SelectCharacter")),
  CharacterCustom:        lazyWithPreload(() => import(/* webpackChunkName: "custom-character" */ "./pages/CharacterCustom")),
  Station1A:              lazyWithPreload(() => import(/* webpackChunkName: "s-1-a" */ "./pages/Station1A")),
  Station1B:              lazyWithPreload(() => import(/* webpackChunkName: "s-1-b" */ "./pages/Station1B")),
  StationCommunicationA:  lazyWithPreload(() => import(/* webpackChunkName: "s-c-a" */ "./pages/StationCommunicationA")),
  StationCommunicationB:  lazyWithPreload(() => import(/* webpackChunkName: "s-c-b" */ "./pages/StationCommunicationB")),
  StationRelationshipA:   lazyWithPreload(() => import(/* webpackChunkName: "s-r-a" */ "./pages/StationRelationshipA")),
  StationRelationshipB:   lazyWithPreload(() => import(/* webpackChunkName: "s-r-b" */ "./pages/StationRelationshipB")),
  StationMusicA:          lazyWithPreload(() => import(/* webpackChunkName: "s-m-a" */ "./pages/Station4MusicA")),
  StationMusicB:          lazyWithPreload(() => import(/* webpackChunkName: "s-m-b" */ "./pages/Station4MusicB")),
  StationFashionA:        lazyWithPreload(() => import(/* webpackChunkName: "s-f-a" */ "./pages/Station4FashionA")),
  StationFashionB:        lazyWithPreload(() => import(/* webpackChunkName: "s-f-b" */ "./pages/Station4FashionB")),
  StationSportA:          lazyWithPreload(() => import(/* webpackChunkName: "s-s-a" */ "./pages/Station4SportA")),
  StationSportB:          lazyWithPreload(() => import(/* webpackChunkName: "s-s-b" */ "./pages/Station4SportB")),
  StationGeekA:           lazyWithPreload(() => import(/* webpackChunkName: "s-g-a" */ "./pages/Station4GeekA")),
  StationGeekB:           lazyWithPreload(() => import(/* webpackChunkName: "s-g-b" */ "./pages/Station4GeekB")),
  StationEndA:            lazyWithPreload(() => import(/* webpackChunkName: "s-e-a" */ "./pages/StationEndA")),
  StationEndB:            lazyWithPreload(() => import(/* webpackChunkName: "s-e-b" */ "./pages/StationEndB")),
  StationEndC:            lazyWithPreload(() => import(/* webpackChunkName: "s-e-c" */ "./pages/StationEndC")),
  StationEndD:            lazyWithPreload(() => import(/* webpackChunkName: "s-e-d" */ "./pages/StationEndD")),
} as const;

// ✅ preload utility
export function preloadPage(pageScreenKey: keyof typeof SCREENS) {
  const Component = SCREENS[pageScreenKey];
  if ("preload" in Component && typeof Component.preload === "function") {
    Component.preload();
  }
}

function useAppInitialConfig() {

  useEffect(() => {

    (function appTypeSetup() {
      const params = new URLSearchParams(window.location.search);

      let appType = params.get("app-type");
      if(!appType) return;

      appType = appType.toLowerCase();
      if(!APP_TYPES.includes(appType as AppType)) {
        appType = AppTypeEnum.WEB;
      }

      const cattownUser = new CattownUser();
      cattownUser.setAppType(appType as AppType);

      params.delete("app-type")

      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState({}, "", newUrl);
      window.location.reload();

    })();

  }, []);

}

function App() {
  const refResize = useRef<HTMLDivElement>(null);

  // Handle query cache reset from URL
  useAppInitialConfig();

  return (
    <div ref={refResize} className="App row justify-content-center align-items-center">
      <QueryClientProvider client={queryClient}   >

      <BrowserRouter>
        <Suspense fallback={<Loading />}>
          {/*<APIProvider apiKey={googleMapsApiKey}> */}

        <Routes>
          <Route path={routes.index} index  element={<SCREENS.Welcome />} />
          <Route path={routes.sponsors}     element={<SCREENS.Sponsors />} />
          <Route path={routes.language}     element={<SCREENS.SelectLanguage />} />
          <Route path={routes.province}     element={<SCREENS.ProvinceMessage />} />
          <Route path={routes.instructions} element={<SCREENS.Instructions />} />
          <Route path={routes.city}         element={<SCREENS.SelectCity />} />
          <Route path={routes.character}    element={<SCREENS.SelectCharacter />} />
          <Route path={routes.character_custom} element={<SCREENS.CharacterCustom />} />
          <Route path={routes.station.station1A}   element={<SCREENS.Station1A/>} />
          <Route path={routes.station.station1B}   element={<SCREENS.Station1B/>} />
          <Route path={routes.station.communicationA} element={<SCREENS.StationCommunicationA/>} />
          <Route path={routes.station.communicationB} element={<SCREENS.StationCommunicationB/>} />
          <Route path={routes.station.relationshipA}  element={<SCREENS.StationRelationshipA />} />
          <Route path={routes.station.relationshipB}  element={<SCREENS.StationRelationshipB />} />
          <Route path={routes.station.musicA} element={<SCREENS.StationMusicA/>} />
          <Route path={routes.station.musicB} element={<SCREENS.StationMusicB/>} />
          <Route path={routes.station.fashionA} element={<SCREENS.StationFashionA/>} />
          <Route path={routes.station.fashionB} element={<SCREENS.StationFashionB/>} />
          <Route path={routes.station.sportA}   element={<SCREENS.StationSportA/>} />
          <Route path={routes.station.sportB}   element={<SCREENS.StationSportB/>} />
          <Route path={routes.station.geekA}    element={<SCREENS.StationGeekA/>} />
          <Route path={routes.station.geekB}    element={<SCREENS.StationGeekB/>} />
          <Route path={routes.station.endA}     element={<SCREENS.StationEndA/>} />
          <Route path={routes.station.endB}     element={<SCREENS.StationEndB/>} />
          <Route path={routes.station.endC}     element={<SCREENS.StationEndC/>} />
          <Route path={routes.station.endD}     element={<SCREENS.StationEndD/>} />
        </Routes>
          {/*</APIProvider> */}
        </Suspense>
      </BrowserRouter>
      </QueryClientProvider>

    </div>
  );
}

export default App;
